import { Storage } from "@capacitor/storage";
import { useFetchUser } from "@multicines/services";
import { CONSTANTS } from "@multicines/utils";
import { events } from "artisn/analytics";
import { getShoppingCart } from "artisn/shopping-cart";
import FacebookSVG from "images/facebook-filled.svg";
import GoogleSVG from "images/google-filled.svg";
import { useRouter } from "next/router";
import React from "react";

import Styles from "./UserData.styles";
import { UserDataProps as Props } from "./UserData.types";
import useI18n from "../../../hooks/useI18n";
import Button from "../Button/Button";
import { notify } from "config/bugsnag.config";
import useAnalytics from "hooks/analytics/useAnalytics";
import useAuth from "hooks/useAuth/useAuth";
import { usePostMigrateAnonymousOrders } from "services/orders/orders.service.hooks";
import { useAuthStore } from "stores/auth/auth.store";
import { useCheckoutStore } from "stores/checkout/checkout.store";
import { sanitizeQueryParams } from "utils/common.utils";
import { dismissErrorNotification } from "utils/notifications.utils";
import { createErrorNotification } from "utils/notifications.utils";

const { logSignIn } = events.auth;
const { SHOPPING_CART_DEFAULT_NAME, ACCOUNT_ID } = CONSTANTS.ARTISN;
const { ANONYMOUS_SHOPPING_CART_TOKEN } = CONSTANTS.STORAGE;

const UserData: React.FC<Props> = props => {
  const { className, isProfile = true } = props;
  const isAnonymous = useAuthStore(state => state.isAnonymous);
  const uid = useAuthStore(state => state.uid);
  const analyticsHook = useAnalytics({
    isSnackWorkflow: false
  });
  const { data: user } = useFetchUser({ isAnonymous, uid });
  const { name = "", lastname = "" } = user ?? {};
  const { push } = useRouter();
  const { asPath, query, replace } = useRouter();
  const { signInWithFacebook, signInWithGoogle } = useAuth();
  const { redirect } = sanitizeQueryParams(query);
  const transferAnonymousId = useCheckoutStore(
    state => state.transferAnonymousId
  );
  const postMigrateAnonymousOrders = usePostMigrateAnonymousOrders();
  const { mutateAsync: migrateAnonymousOrders, reset } =
    postMigrateAnonymousOrders;
  const t = useI18n();

  const { logSelectRegistration, commonParams } = analyticsHook;

  const handleRedirection = () => {
    const target = `${redirect ?? "/"}`;
    if (asPath === target) return;
    replace(target);
  };

  const setAnonymousCartStorage = async () => {
    const anonymousCart = await getShoppingCart({
      shoppingCartName: SHOPPING_CART_DEFAULT_NAME,
      anonymous: isAnonymous!,
      accountId: ACCOUNT_ID,
      customerId: uid!
    });
    if (!anonymousCart) return;
    await Storage.set({
      key: ANONYMOUS_SHOPPING_CART_TOKEN,
      value: JSON.stringify(anonymousCart)
    });
  };

  const signInWithGoogleHandler = async () => {
    logSelectRegistration({ type: "Google" });
    try {
      setAnonymousCartStorage();
      const { user } = await signInWithGoogle();
      if (!user) throw new Error("No user was found with Google Provider");
      const { displayName } = user;

      logSignIn({ method: "Google", name: displayName ?? "", ...commonParams });
      if (transferAnonymousId) {
        migrateAnonymousOrders();
        reset();
      }
      handleRedirection();
      return;
    } catch (e) {
      notify(e, "signInWithGoogleHandler - UserData");
      dismissErrorNotification();
      createErrorNotification(
        "No se encontró ningún usuario con el proveedor de Google"
      );
    }
  };

  const signInWithFacebookHandler = async () => {
    logSelectRegistration({ type: "Facebook" });
    try {
      setAnonymousCartStorage();
      const { user } = await signInWithFacebook();
      if (!user) throw new Error("No user was found with Facebook Provider");
      const { displayName } = user;

      logSignIn({
        method: "Facebook",
        name: displayName ?? "",
        ...commonParams
      });
      if (transferAnonymousId) {
        migrateAnonymousOrders();
        reset();
      }
      handleRedirection();
      return;
    } catch (e) {
      notify(e, "signInWithFacebookHandler - UserData");
      dismissErrorNotification();
      createErrorNotification(
        "No se encontró ningún usuario con el proveedor de Facebook"
      );
    }
  };

  const handleEmailSignIn = async () => {
    logSelectRegistration({ type: "Email" });
    if (!isAnonymous) return;
    push("/auth-otp");
  };

  return (
    <Styles
      className={`UserData ${className}`}
      isProfile={isProfile}
      isAnonymous={isAnonymous}
    >
      <div className="UserData__header">
        {isAnonymous && isProfile ? (
          <>
            <p className="UserData__header__title">
              {t.greetings.profileAnonymous}
            </p>
            <p className="UserData__header__description">
              {t.greetings.profileAnonymousDescription}
            </p>
            <div className="UserData__buttons">
              <div
                className="UserData__buttons__social"
                onClick={signInWithGoogleHandler}
              >
                <GoogleSVG />
              </div>
              <div
                className="UserData__buttons__social"
                onClick={signInWithFacebookHandler}
              >
                <FacebookSVG />
              </div>
              {/* <div  className="UserData__buttons__social">
                <AppleSVG />
              </div> */}
              <Button mode="SECONDARY" onClick={handleEmailSignIn}>
                {t.common.emailAddress}
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="UserData__header__hello">
              {isProfile || isAnonymous
                ? `${t.greetings.profile},`
                : `${name},`}
            </p>
            <p className="UserData__header__text">
              {isProfile ? `${name} ${lastname}` : `${t.greetings.simple}`}
            </p>
          </>
        )}
      </div>
    </Styles>
  );
};

export default UserData;
